import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import clsx from 'clsx';
import { motion } from 'motion/react';
import { duration } from './primitives/tokens';
import { Pressable, type PressEffect } from './primitives/motion';
import { useControllable } from '../../hooks/useControllable';
import styles from './Segmented.module.css';

type SegmentedSize = 'sm' | 'md' | 'lg';

interface SegmentedOption {
  label: React.ReactNode;
  value: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

const segmented = cva(styles.base, {
  variants: {
    block: { true: styles.block, false: styles.inline },
    ghost: { true: styles.ghost, false: styles.solid },
  },
  defaultVariants: { block: false, ghost: true },
});

interface SegmentedProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>, VariantProps<typeof segmented> {
  /** Controlled value */
  value?: string;
  /** Default value */
  defaultValue?: string;
  /** Options */
  options: (string | SegmentedOption)[];
  /** Change handler */
  onChange?: (value: string) => void;
  /** Disabled */
  disabled?: boolean;
  /** Full width */
  block?: boolean;
  /** Size */
  size?: SegmentedSize;
  /** Transparent background — no track, just floating pills */
  ghost?: boolean;
  /**
   * Press feedback effect. Opt-in — defaults to `'none'`.
   * - `'ring'` — border ring pulses outward on segment click
   * - `'glow'` — radial halo expands from click point
   * - `'none'` — no press effect (default)
   */
  pressEffect?: PressEffect;
}

const sizeMap: Record<SegmentedSize, { height: string; fontSize: string; padding: string }> = {
  sm: { height: 'var(--control-height-sm)', fontSize: 'var(--text-sm)', padding: '0 var(--space-2)' },
  md: { height: 'var(--control-height-md)', fontSize: 'var(--text-base)', padding: '0 var(--space-3)' },
  lg: { height: 'var(--control-height-lg)', fontSize: 'var(--text-md)', padding: '0 var(--space-4)' },
};

/**
 * Segmented — inline option selector (button group with single selection).
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | value | string | — | Controlled value |
 * | defaultValue | string | — | Initial value |
 * | options | (string \| { label, value, icon?, disabled? })[] | — | Options |
 * | onChange | (value) => void | — | Change handler |
 * | disabled | boolean | false | Disable all |
 * | block | boolean | false | Full width |
 * | size | 'sm' \| 'md' \| 'lg' | 'md' | Size |
 *
 * @example
 * <Segmented options={['Day', 'Week', 'Month']} />
 * <Segmented options={[
 *   { label: 'List', value: 'list', icon: <List size={14} /> },
 *   { label: 'Grid', value: 'grid', icon: <Grid size={14} /> },
 * ]} />
 */
export function Segmented({
  value: controlledValue,
  defaultValue,
  options,
  onChange,
  disabled = false,
  block = false,
  size = 'md',
  ghost = true,
  pressEffect = 'none',
  className,
  style,
  ...props
}: SegmentedProps) {
  const normalized = options.map((opt) => (typeof opt === 'string' ? { label: opt, value: opt } : opt));
  const [value, setValue] = useControllable(controlledValue, defaultValue ?? normalized[0]?.value ?? '', onChange);
  // Unique per instance: the sliding active-indicator uses a Motion layoutId,
  // which is GLOBAL. A shared literal id makes the highlight animate BETWEEN
  // separate Segmented components on the same page (e.g. two toggles under a
  // modal). Scoping the id per instance keeps each toggle's indicator its own.
  const layoutId = `segmented-active-${React.useId()}`;

  return (
    <div
      className={segmented({ block, ghost, className })}
      style={style}
      {...props}
    >
      {normalized.map((opt) => {
        const isActive = opt.value === value;
        const isDisabled = disabled || opt.disabled;
        return (
          <SegmentedItem
            key={opt.value}
            option={opt}
            active={isActive}
            disabled={isDisabled ?? false}
            size={size}
            block={block}
            ghost={ghost}
            pressEffect={pressEffect}
            layoutId={layoutId}
            onClick={() => !isDisabled && setValue(opt.value)}
          />
        );
      })}
    </div>
  );
}

function SegmentedItem({
  option,
  active,
  disabled,
  size,
  block,
  ghost,
  pressEffect,
  layoutId,
  onClick,
}: {
  option: SegmentedOption;
  active: boolean;
  disabled: boolean;
  size: SegmentedSize;
  block: boolean;
  ghost: boolean;
  pressEffect: PressEffect;
  layoutId: string;
  onClick: () => void;
}) {
  const sz = sizeMap[size];

  return (
    <Pressable effect={pressEffect} disabled={disabled}>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-pressed={active}
        className={clsx(
          styles.item,
          active && styles.itemActive,
          active && !ghost && styles.itemActiveNonGhost,
          disabled && styles.itemDisabled,
          block && styles.itemBlock,
        )}
        style={{
          '--seg-height': ghost ? undefined : sz.height,
          '--seg-padding': ghost ? 'var(--space-3) var(--space-5)' : sz.padding,
          '--seg-font-size': ghost ? 'var(--text-sm)' : sz.fontSize,
        } as React.CSSProperties}
      >
        {active && (
          <motion.div
            layoutId={layoutId}
            className={styles.indicator}
            transition={{ type: 'tween', duration: duration.normal / 1000 }}
          />
        )}
        {option.icon && <span className={styles.itemIcon}>{option.icon}</span>}
        {option.label}
      </button>
    </Pressable>
  );
}
